import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '@material-ui/core/Button';
import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import { AttachFile, HighlightOffRounded } from '@material-ui/icons';
import { useSelector } from 'react-redux';
import { GlobalWorkerOptions } from 'pdfjs-dist/legacy/build/pdf';
import { getProjectState } from '../../features/project/selectors';
import { FileNode, FileNodeTypeEnum, IProject } from '../../api-client/autogenerated';
import FileTree from '../design/FileTree';
import RemovableChip from '../custom-components/RemovableChip';

// @ts-ignore
import pdfjsWorker from 'pdfjs-dist/legacy/build/pdf.worker.entry';

GlobalWorkerOptions.workerSrc = pdfjsWorker;

interface AddDesignFileDialogProps {
  open: boolean;
  handleClose: () => void;
  title: string;
  acceptAll?: boolean;
  handleDesignAttach?: (files: FileNode[]) => void;
  singleFile?: boolean;
  overrideProject?: IProject;
  allowOnlyPdfs?: boolean;
}

const useStyles = makeStyles((theme) => ({
  paper: {
    maxWidth: 1500,
  },
  root: {
    width: '100%',
  },
  title: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: '26px',
    lineHeight: '30px',
    textAlign: 'left',
    color: '#0947B9',
    marginBottom: '8px',
    paddingLeft: 8,
  },
  subtitle: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: '18px',
    lineHeight: '21px',
    textAlign: 'left',
    textTransform: 'capitalize',
    color: '#464546',
    paddingLeft: 8,
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: '-16px',
  },
  contentContainer: {},
  rootIconButton: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  textfield: {
    width: '320px',
    height: '260px',
  },
  actions: {
    padding: '8px 29px 28px',
    // On a phone the chips and the Attach button cannot share a line: the button is pushed to a
    // sliver and the names are squeezed with it. What has been picked goes above the button that
    // acts on it.
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  dragDropText: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: '22px',
    lineHeight: '0px',
    textAlign: 'center',
    textTransform: 'none',
    color: '#949494', // Gray 400
  },
  browseFileButton: {
    border: '2px solid #0947B9',
    boxSizing: 'border-box',
    borderRadius: '4px',
    color: '#0947B9',
  },
  dropzoneStyling: {
    width: '100%',
    flexShrink: 0,
    background: '#F9F9F9',
    mixBlendMode: 'normal',
    border: '2px dashed #949494',
    boxSizing: 'border-box',
    borderRadius: '4px',
    paddingBottom: 24,
  },
  file: {
    display: 'inline-flex',
    marginTop: '16px',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fileText: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '11px',
    lineHeight: '13px',
    textAlign: 'center',
    textTransform: 'none',
    color: '#949494', // Gray 400
    paddingLeft: 8,
  },
  columnLayout: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  rowLayout: {
    display: 'flex',
    flexWrap: 'nowrap',
  },
  // The chips share the actions row with the Attach button, so they take the space left over and
  // scroll once a big selection outgrows it rather than pushing the button off the dialog.
  selectionSummary: {
    flexGrow: 1,
    minWidth: 0,
    maxHeight: 96,
    overflowY: 'auto',
    marginRight: 8,
    [theme.breakpoints.down('xs')]: {
      marginRight: 0,
      marginBottom: 8,
      maxHeight: 140,
    },
  },
  selectedChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  noSelection: {
    color: '#949494', // Gray 400
    fontStyle: 'italic',
  },
}));

export default function AddDesignFileDialog(props: AddDesignFileDialogProps) {
  const classes = useStyles();
  const project = useSelector(getProjectState);

  const {
    open,
    handleClose,
    acceptAll,
    handleDesignAttach = (files: FileNode[]) => {},
    title,
    singleFile = false,
    overrideProject,
    allowOnlyPdfs,
  } = props;

  const [selectedFiles, setSelectedFiles] = useState<FileNode[]>([]);
  const [canSubmit, setCanSubmit] = useState<boolean>(false);

  // Ticking a folder selects it alongside its contents; only the files are worth naming back.
  const selectedFileNodes = useMemo(
    () => selectedFiles.filter((file) => file.type === FileNodeTypeEnum.File),
    [selectedFiles],
  );

  const handleFileSelect = (files: FileNode[]) => {
    setSelectedFiles(files);
  };

  // The tree owns the checkboxes, so removing a chip has to clear the tick there rather than only
  // dropping it from this list -- otherwise the row stays checked and Attach still sends the file.
  const deselectFromTree = useRef<((fullKey: string) => void) | null>(null);
  const provideDeselect = useCallback((deselect: (fullKey: string) => void) => {
    deselectFromTree.current = deselect;
  }, []);

  useEffect(() => {
    // TO-DO: proper managment of which files are newly selected
    setCanSubmit(selectedFiles.length > 0);
  }, [selectedFiles]);

  return (
    <>
      <Dialog
        fullWidth
        open={open}
        onClose={handleClose}
        classes={{ paper: classes.paper }}
        className={classes.root}
      >
        <DialogTitle>
          <div className={classes.titleContainer}>
            <span className={classes.title}>{title}</span>
            <span className={classes.subtitle}>Attach Your Files</span>
          </div>
          <IconButton
            style={{ right: '24px', top: '24px', position: 'absolute' }}
            onClick={handleClose}
            classes={{
              root: classes.rootIconButton,
            }}
          >
            <HighlightOffRounded />
          </IconButton>
        </DialogTitle>
        <DialogContent style={{ flexDirection: 'column' }} className={classes.contentContainer}>
          <div>
            {/*
              A picker, not the Drive itself: everything a row otherwise offers -- viewing,
              downloading, sharing, the activity log, the options menu -- acts on the file where it
              lives rather than on the choice being made here, so none of it is offered and the
              columns go with it. Folders are opened, not picked, so they carry no checkbox.
            */}
            <FileTree
              allowOnlyPdfs={allowOnlyPdfs}
              project={overrideProject || project}
              small
              type="Project Files"
              handleFileSelect={handleFileSelect}
              provideDeselect={provideDeselect}
              disableSelectAll
              disableOptions
              disableUploadFile
              selectionOnly
              singleFile={singleFile}
            />
          </div>
        </DialogContent>
        <DialogActions className={classes.actions}>
          {/*
            The files themselves rather than a count of them: the tree scrolls, so a count was the
            only record of what had been ticked and gave no way to notice a wrong one -- or to drop
            it without hunting the row back down. Removing a chip unticks that row in the tree.
          */}
          <div className={classes.selectionSummary}>
            {selectedFileNodes.length > 0 ? (
              <div className={classes.selectedChips}>
                {selectedFileNodes.map((file) => (
                  <RemovableChip
                    key={file.fullKey}
                    typeIcon={<AttachFile fontSize="small" />}
                    label={file.fullKey.split('/').pop() || file.fullKey}
                    onRemove={() => deselectFromTree.current?.(file.fullKey)}
                  />
                ))}
              </div>
            ) : (
              <div className={classes.noSelection}>
                No files selected{acceptAll ? '' : ' (Only PDFs allowed)'}
              </div>
            )}
          </div>
          <Button
            onClick={() => {
              handleDesignAttach(selectedFiles);
              setSelectedFiles([]);
              handleClose();
            }}
            variant="contained"
            color="primary"
            disabled={!canSubmit}
            style={{ flexShrink: 0 }}
            startIcon={<AttachFile style={{ transform: 'scaleY(-1) rotate(45deg)' }} />}
          >
            ATTACH
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
